import {FulfillmentType, PlacedOrderRestaurantLocation} from "@/data/entities/restaurant";
import {AddToCartProduct, PlacedOrderProduct} from "@/data/entities/product";
import {BusinessDetails, CustomerSearchResult, PlacedOrderUser} from "@/data/entities/user";
import {OrderPlacementDeliveryAddress} from "@/data/entities/deliveryAddress";
import {Validation} from "@/data/Validation";

export type OrderSource = "WEBSITE" | "POS";

export type PaymentMethod = "ONLINE" | "IN_PERSON";

/** Requested time: as soon as possible. */
export const REQUESTED_TIME_ASAP = "ASAP";

/** Requested time: the customer is already in the restaurant (only takeaway via the POS). */
export const REQUESTED_TIME_PRESENT = "PRESENT";

/**
 * Payment of an order paid in person, in the order editor: still to pay at
 * the door or at pickup, or already paid in the restaurant.
 */
export type InPersonPaymentStatus = "PAY_LATER" | "PAID";

/**
 * How a manual discount (via the POS) is expressed: a fixed amount of money,
 * or a percentage of the total.
 */
export type DiscountType = "EURO" | "PERCENTAGE";

/**
 * Manual discount on the total of an order (without the tip). The backend
 * calculates the amount: never more than the total.
 */
export type PosOrderDiscount = {
    type: DiscountType,
    value: number, // Money for EURO, percentage (at most 100) for PERCENTAGE
};

/** Highest discount percentage. */
export const MAX_DISCOUNT_PERCENTAGE = 100;

/** Highest euro discount the backend accepts. */
export const MAX_DISCOUNT_EURO = 999999.99;

/**
 * Receipts of an existing order to print via the print agent. At least one
 * must be `true`.
 */
export type PosOrderPrint = {
    customerReceipt: boolean,
    kitchenReceipt: boolean,
    deliveryReceipt: boolean,
    courierCode?: string | null,
};

export type PosOrderSummary = {
    cartPublicId: string,
    cartId: number,
    source: OrderSource,
    firstName: string,
    lastName: string,
    phoneNumber: string | null, // Optional for orders via the POS
    emailAddress: string | null, // Optional for orders via the POS
    account: string | null,
    delivery: boolean,
    street: string | null,
    streetNumber: string | null,
    city: string | null,
    requestedTime: string,
    fulfillmentDate: string, // YYYY-MM-DD: day of delivery or pickup; later than the creation date when ordered in advance
    creationDate: string,
    paymentMethod: PaymentMethod,
    paidWith: string | null,
    totalToPay: number,
    discountAmount: number | null, // Money subtracted by a manual discount; null without discount
    paidInPersonOn: string | null, // When paid in the restaurant; null when still to pay at the door or at pickup
    cancelledOn: string | null,
    refundedOn: string | null,
};

export type PlacedOrderOverview = {
    user: PlacedOrderUser | null;
    cartId: number;
    cartPublicId: string;
    restaurant: PlacedOrderRestaurantLocation;
    firstName: string;
    lastName: string;
    phoneNumber: string | null;
    emailAddress: string | null;
    source: OrderSource,
    delivery: boolean;
    note: string | null;
    requestedTime: string;
    fulfillmentDate: string; // YYYY-MM-DD: day of delivery or pickup
    creationDate: string; // OffsetDateTime serializes to ISO 8601 string
    paymentMethod: PaymentMethod;
    subtotal: number;
    deposit: number;
    supFee: number;
    tip: number;
    tipPercentage: number;
    deliveryFee?: number | null;
    discountType: DiscountType | null, // Manual discount via the POS; null without discount
    discountValue: number | null, // As entered: money or percentage
    discountAmount: number | null, // Money subtracted from the total
    earnedLoyaltyPoints: number,
    redeemedLoyaltyPoints: number,
    totalToPay: number;
    amountPaidOnline: number | null, // Amount paid online via Mollie, `null` when paid in person. Not changed by updates.
    amountToPayInPerson: number, // What the customer still pays in person (PIN): the total, or what it went up after paying online
    amountToRefund: number, // What to refund, because the total went down after paying online
    paidInPersonOn: string | null,
    courier: PlacedOrderCourier | null, // Employee who delivers the order, linked when printing the delivery receipt
    courierAssignedOn: string | null,
    paidWith: string | null,
    vatLowAmount: number,
    vatLow: number,
    vatHighAmount: number,
    vatHigh: number,
    vatNoneAmount: number,
    cancelledOn: string | null,
    deliveryAddress: PlacedOrderDeliveryAddress | null;
    businessDetails: PlacedOrderBusinessDetails | null;
    products: PlacedOrderProduct[];
};

export type PlacedOrderDeliveryAddress = {
    name?: string;
    street: string;
    streetNumber: string;
    postalCodeDigits: string;
    postalCodeLetters: string;
    city: string;
    extraAddressInfo?: string | null;
};

export type PlacedOrderBusinessDetails = {
    organisationName: string;
    taxNumber?: string;
    cocNumber?: string;
    emailAddress?: string;
    street?: string;
    streetNumber?: string;
    postalCodeDigits?: string;
    postalCodeLetters?: string;
    city?: string;
    extraAddressInfo?: string;
};

export type PosOrderGuest = {
    firstName: string,
    lastName: string,
    phoneNumber: string | null, // Optional
    emailAddress: string | null, // Optional; without it, the customer gets no mails
};

export type OrderPlacementBusinessDetails = {
    isBusinessOrder: boolean,
    organisationName: string | null,
    vatNumber: string | null,
    cocNumber: string | null,
    emailAddress: string | null,
    street: string | null,
    streetNumber: string | null,
    postalCodeDigits: string | null,
    postalCodeLetters: string | null,
    city: string | null,
    extraAddressInfo: string | null,
};

// export type PosOrderPlacement = {
//     products: AddToCartProduct[],
//     // Either customerId OR guest must be set:
//     customerId: number | null,
//     guest: PosOrderGuest | null,
//     businessDetails: OrderPlacementBusinessDetails | null, // Null is not business order
//     deliveryAddress: OrderPlacementDeliveryAddress,
//     requestedTime: string,
//     note: string | null,
// }
//
// export type PosOrderUpdate = {
//     products: AddToCartProduct[],
//     contactDetails: PosOrderGuest,
//     deliveryAddress: OrderPlacementDeliveryAddress,
//     businessDetails: PlacedOrderBusinessDetails,
//     requestedTime: string,
//     note: string | null,
// };

export type CustomerType = "EXISTING" | "GUEST";

// /**
//  * Values of the order editor form, for both placing and updating an order.
//  * `contactDetails` is always filled (prefilled from the account when one is
//  * selected).
//  */
// export type PosOrderForm = PosOrderUpdate & {
//     customerType: CustomerType,
//     customerId: number | null,
// };

export type PosOrderCancellation = {
    reason: string,
    // Only for orders paid online: the refund in Mollie is already done
    refunded: boolean,
};

export const orderCancellationValidation: Validation<Pick<PosOrderCancellation, "reason">> = {
    reason: {
        required: true,
        minLength: 1,
        maxLength: 256,
    },
};

export const orderPlacementValidation: Validation<Pick<PosOrderPlacement, "note">> = {
    note: {
        required: false,
        minLength: 1,
        maxLength: 512,
    },
};

export const orderPersonValidation: Validation<PosOrderGuest> = {
    firstName: {
        required: true,
        minLength: 1,
        maxLength: 128,
    },
    lastName: {
        required: true,
        minLength: 1,
        maxLength: 128,
    },
    phoneNumber: {
        required: false,
        minLength: 1,
        maxLength: 128,
    },
    emailAddress: {
        required: false,
        minLength: 1,
        maxLength: 128,
    },
};

/**
 * Business details of a POS order. Only `organisationName` is required; the
 * address is required when the order is delivered to the business address
 * (`deliveryAddress.type === "BUSINESS"`). Not saved to the customer account.
 */
export type PosOrderBusinessDetails = BusinessDetails;

export const emptyBusinessDetails = (): PosOrderBusinessDetails => ({
    organisationName: "",
    emailAddress: null,
    taxNumber: null,
    cocNumber: null,
    street: null,
    streetNumber: null,
    postalCodeDigits: null,
    postalCodeLetters: null,
    city: null,
    extraAddressInfo: null,
});

export type PosOrderPlacement = {
    products: AddToCartProduct[],
    // Either customerId OR guest must be set:
    customerId: number | null,
    guest: PosOrderGuest | null,
    deliveryAddress: OrderPlacementDeliveryAddress,
    businessDetails: PosOrderBusinessDetails | null,
    requestedTime: string,
    note: string | null,
    paidInPerson: boolean, // Already paid in the restaurant
    discount: PosOrderDiscount | null,
}

export type PosOrderUpdate = {
    products: AddToCartProduct[],
    contactDetails: PosOrderGuest,
    deliveryAddress: OrderPlacementDeliveryAddress,
    // Replace semantics: null removes the business details of the order
    businessDetails: PosOrderBusinessDetails | null,
    requestedTime: string,
    note: string | null,
    paidInPerson: boolean, // Paid in the restaurant; only for orders paid in person
    // Replace semantics: null removes the discount of the order
    discount: PosOrderDiscount | null,
};

/**
 * An existing order to load into the order editor.
 */
export type PosOrderEdit = {
    order: PosOrderUpdate,
    // Account the order belongs to; can not be changed
    customer: CustomerSearchResult | null,
    // Can not be changed
    fulfillmentType: FulfillmentType,
    paymentMethod: PaymentMethod,
    totalToPay: number,
    amountPaidOnline: number | null,
    // Website orders keep their tip percentage; the tip is calculated over the new totals
    tipPercentage: number,
};

export type PosOrderForm = Omit<PosOrderUpdate, "paidInPerson"> & {
    paymentStatus: InPersonPaymentStatus,
    customerType: CustomerType,
    customerId: number | null,
    // Only for the editor (addresses, business details); not sent to the backend
    customer: CustomerSearchResult | null,
};

/**
 * Employee who delivers an order.
 */
export type PlacedOrderCourier = {
    id: number,
    firstName: string,
    lastName: string,
};

export const businessDetailsValidation: Validation<Pick<PosOrderBusinessDetails,
    "organisationName" | "emailAddress" | "taxNumber" | "cocNumber">> = {
    organisationName: {required: true, minLength: 1, maxLength: 100},
    emailAddress: {required: false, maxLength: 128},
    taxNumber: {required: false, maxLength: 56},
    cocNumber: {required: false, maxLength: 56},
};
